// Module: usePriceSparkline.js
// Purpose: Last-30-minute price series for the header sparkline — today's 1m closes
//          (fetched once, refreshed every 5 min) plus the live LTP appended as newest point.
//          Returns {t,v} points (unix s, price) so PriceSparkline places them on a TIME axis:
//          1m closes and 5 s live samples never share an index axis again (the live tail used
//          to render stretched ~12x relative to history).

import { useEffect, useMemo, useRef, useState } from 'react';
import { services } from '../../services/api';
import { toUnixSec } from '../../utils/tradeMarkerBuilder';
import { WINDOW_SEC } from '../../components/trading/PriceSparkline';

export { WINDOW_SEC };
export const REFRESH_MS = 5 * 60 * 1000;
export const LIVE_THROTTLE_MS = 5000;

const utcDate = (ms) => new Date(ms).toISOString().slice(0, 10);

/**
 * Keep closes whose bar time lies within the last 30 minutes. Exported for tests.
 * @returns {{t:number,v:number}[]} ascending by time
 */
export function sliceWindow(rows, nowSec) {
  return (Array.isArray(rows) ? rows : [])
    .map((r) => ({ t: toUnixSec(r.time ?? r.timestamp), v: Number(r.close) }))
    .filter((p) => p.t != null && Number.isFinite(p.v) && p.t >= nowSec - WINDOW_SEC && p.t <= nowSec)
    .sort((a, b) => a.t - b.t);
}

/**
 * @param {string|number|null} instrumentId
 * @param {number|null} liveLtp
 * @returns {{t:number,v:number}[]} points oldest -> newest ([] on error / no data)
 */
export function usePriceSparkline(instrumentId, liveLtp) {
  const [bars, setBars] = useState([]);
  const [livePts, setLivePts] = useState([]);
  const lastLiveMs = useRef(0);

  //--> History: fetch once, refetch every 5 minutes; abort on unmount. Every series value is
  //*   reset on an instrument change so a rolled/re-selected deployment never charts the previous
  //*   contract's bars or live samples beside the new price pill (Codex review F5).
  useEffect(() => {
    setBars([]);
    setLivePts([]);
    lastLiveMs.current = 0;
    if (!instrumentId) {
      return undefined;
    }
    let active = true;
    let controller = null;
    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      const nowMs = Date.now();
      try {
        const res = await services.marketData.getDateRangeData(
          instrumentId,
          {
            start_date: utcDate(nowMs - WINDOW_SEC * 1000),
            end_date: utcDate(nowMs),
            timeframe: '1m',
          },
          null,
          { signal: controller.signal },
        );
        const body = res?.data;
        const rows = Array.isArray(body?.data)
          ? body.data
          : (Array.isArray(body?.data?.data) ? body.data.data : []);
        if (active) setBars(sliceWindow(rows, Math.floor(nowMs / 1000)));
      } catch (err) {
        console.log('[DIAG] price sparkline fetch failed', { instrumentId, err: err?.message });
        if (active) setBars([]);
      }
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      clearInterval(timer);
      controller?.abort();
    };
  }, [instrumentId]);

  //--> Live LTP: one point per 5 s, capped to the 30 minute window
  useEffect(() => {
    const v = Number(liveLtp);
    if (liveLtp == null || !Number.isFinite(v)) return;
    const nowMs = Date.now();
    if (nowMs - lastLiveMs.current < LIVE_THROTTLE_MS) return;
    lastLiveMs.current = nowMs;
    const cutoff = Math.floor(nowMs / 1000) - WINDOW_SEC;
    setLivePts((prev) => [...prev.filter((p) => p.t >= cutoff), { t: Math.floor(nowMs / 1000), v }]);
  }, [liveLtp]);

  return useMemo(() => {
    const cutoff = Math.floor(Date.now() / 1000) - WINDOW_SEC;
    const lastBar = bars.length ? bars[bars.length - 1].t : 0;
    //  live points older than the newest bar are already represented by the bar
    const live = livePts.filter((p) => p.t >= cutoff && p.t > lastBar);
    return [...bars.filter((p) => p.t >= cutoff), ...live];
  }, [bars, livePts]);
}

export default usePriceSparkline;
